import { useQuery } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Download, MapPin, Users, Mail } from "lucide-react";
import type { WaitlistEntry } from "@shared/schema";
import { stripHtml } from "@/lib/sanitize";

interface WaitlistEntryWithPosition extends WaitlistEntry {
  position: number;
}

interface WaitlistGroup {
  state: string;
  entries: WaitlistEntryWithPosition[];
}

function formatDate(date: Date | string | null): string {
  if (!date) return "-";
  return new Date(date).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function WaitlistTable() {
  const { data: waitlistGroups, isLoading } = useQuery<WaitlistGroup[]>({
    queryKey: ["/api/admin/waitlist"],
  });

  const handleExport = () => {
    window.open("/api/admin/waitlist/export", "_blank");
  };

  if (isLoading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-10 w-32" />
        </div>
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      </Card>
    );
  }

  const totalCount = waitlistGroups?.reduce((sum, group) => sum + group.entries.length, 0) || 0;

  return (
    <Card className="p-6" data-testid="waitlist-by-state">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold">Waitlist</h3>
          <p className="text-sm text-muted-foreground">
            {totalCount} total signups across {waitlistGroups?.length || 0} states
          </p>
        </div>
        <Button variant="outline" onClick={handleExport} disabled={totalCount === 0} data-testid="btn-export-csv">
          <Download className="h-4 w-4 mr-2" />
          Export CSV
        </Button>
      </div>

      {!waitlistGroups || waitlistGroups.length === 0 ? (
        <div className="text-center py-12">
          <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">No waitlist entries yet</p>
          <p className="text-sm text-muted-foreground mt-1">
            Customers outside Colorado can join the waitlist
          </p>
        </div>
      ) : (
        <Accordion type="single" collapsible className="w-full" data-testid="waitlist-accordion">
          {waitlistGroups.map((group) => (
            <AccordionItem key={group.state} value={group.state} data-testid={`waitlist-state-${group.state}`}>
              <AccordionTrigger className="hover:no-underline" data-testid={`waitlist-trigger-${group.state}`}>
                <div className="flex items-center justify-between w-full pr-4">
                  <div className="flex items-center gap-3">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">{group.state}</span>
                  </div>
                  <Badge variant="secondary" className="ml-auto">
                    {group.entries.length} {group.entries.length === 1 ? "signup" : "signups"}
                  </Badge>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <Table data-testid={`waitlist-table-${group.state}`}>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Biome Preference</TableHead>
                      <TableHead>Position</TableHead>
                      <TableHead>Joined</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {group.entries.map((entry) => (
                      <TableRow key={entry.id} data-testid={`waitlist-entry-${entry.id}`}>
                        <TableCell className="font-medium">{stripHtml(entry.name)}</TableCell>
                        <TableCell>
                          <a
                            href={`mailto:${encodeURIComponent(stripHtml(entry.email) || '')}`}
                            className="flex items-center gap-1 text-primary hover:underline"
                          >
                            <Mail className="h-3 w-3" />
                            {stripHtml(entry.email)}
                          </a>
                        </TableCell>
                        <TableCell>{stripHtml(entry.phone) || "-"}</TableCell>
                        <TableCell>
                          {entry.biomePreference ? (
                            <Badge variant="outline" className="capitalize">
                              {stripHtml(entry.biomePreference)}
                            </Badge>
                          ) : (
                            "-"
                          )}
                        </TableCell>
                        <TableCell>
                          {entry.position ? `#${entry.position}` : "-"}
                        </TableCell>
                        <TableCell>{formatDate(entry.createdAt)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      )}
    </Card>
  );
}
